<template>
    <Layout>
        <div class="relative isolate min-h-[100svh] overflow-hidden bg-slate-50 pb-6 pt-12 lg:pt-28">
            <div
                class="fixed left-0 top-[10rem] -z-10 mt-96 origin-top-left -rotate-90 transform-gpu opacity-20 blur-3xl sm:ml-20 sm:mt-20 sm:translate-y-0 sm:rotate-0 sm:transform-gpu sm:opacity-40"
                aria-hidden="true"
            >
                <div
                    class="aspect-[1154/678] w-[72.125rem] bg-gradient-to-br from-primary-500/50 to-primary"
                    style="
                        clip-path: polygon(
                            100% 38.5%,
                            82.6% 100%,
                            60.2% 37.7%,
                            52.4% 32.1%,
                            47.5% 41.8%,
                            45.2% 65.6%,
                            27.5% 23.4%,
                            0.1% 35.3%,
                            17.9% 0%,
                            27.7% 23.4%,
                            76.2% 2.5%,
                            74.2% 56%,
                            100% 38.5%
                        );
                    "
                ></div>
            </div>

            <div class="absolute left-0 top-0 mt-6 flex w-full justify-center md:ml-4 md:justify-start">
                <a class="flex cursor-pointer items-center rounded-lg px-2 text-center font-medium" href="/">
                    <PodcastclipfinderIcon class="w-6 shrink-0 fill-current text-gray-900 md:h-10" />
                    <span class="ml-2 text-xl font-black tracking-wide text-gray-900 md:ml-2">Clipfinder</span>
                </a>
            </div>

            <div class="absolute right-4 top-0 mt-4 flex w-full justify-end md:ml-4 md:mt-6 md:justify-end">
                <GoogleLoginButton v-if="!loggedIn" :hideTextOnMobile="true" />
                <EmailLoginButton v-if="!loggedIn" :hideTextOnMobile="true" />
                <span v-else :href="route('clipfinder.conversations.create')" class="flex items-center justify-center overflow-hidden">
                    <img
                        v-if="user?.avatar_url"
                        :src="user.avatar_url"
                        alt="User Avatar"
                        class="h-10 w-10 cursor-pointer rounded-full bg-gray-600 object-cover"
                        @click="router.visit(route('clipfinder.conversations.create'))"
                    />
                </span>
            </div>

            <!--
                CLIP-34. The hero exists to get someone typing. Everything that
                competed with the input for attention (the stack of podcaster
                portraits and the "+194" disc) is gone; what remains says, in
                one breath, what Clipfinder does for you and then hands you the
                input. The podcaster count survives as a quiet link so the
                catalogue stays one click away. The content stream below and
                the SearchInput itself are deliberately untouched.
            -->
            <div class="h-full justify-center lg:flex lg:flex-col">
                <div class="mx-auto mt-12 w-full max-w-4xl px-4 text-center sm:px-6 md:mt-16 lg:mt-0 lg:px-8">
                    <p class="text-xs font-semibold uppercase tracking-[0.2em] text-primary-700 md:text-sm">Podcasts, searched by AI</p>

                    <h1 class="mt-4 text-balance text-3xl font-black leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-[3.5rem]">
                        Skip to the part of the podcast you came for.
                    </h1>

                    <p class="mx-auto mt-5 max-w-2xl text-balance text-base leading-relaxed text-slate-600 md:text-xl">
                        Type a topic. Clipfinder searches {{ hours.toLocaleString('en-US') }} hours of {{ podcasts.toLocaleString('en-US') }} podcasts
                        and drops you at the exact moment it comes up, so you never sit through three hours for the five minutes you wanted.
                    </p>
                </div>

                <div class="mx-auto mt-8 w-full max-w-2xl px-4 sm:px-6 md:mt-10 lg:px-8">
                    <form @submit.prevent="submit" class="relative">
                        <SearchInput
                            :rounded-full="true"
                            v-model="question"
                            @submit="submit"
                            :disabled="isWaiting"
                            placeholder="Prompt here to find clips..."
                            :error="failed || errors.hasOwnProperty('question') || false"
                        />
                    </form>

                    <p class="mt-3 text-center text-xs text-slate-500 md:text-sm">
                        Searching across
                        <Link
                            :href="route('clipfinder.podcasters.index')"
                            class="font-medium text-slate-700 underline decoration-slate-300 underline-offset-2 hover:decoration-primary"
                        >
                            {{ totalPodcastersCount.toLocaleString('en-US') }} podcasters
                        </Link>
                        &middot;
                        <Link
                            :href="route('clipfinder.conversations.create', { openSidebar: 1 })"
                            :only="['conversations']"
                            class="font-medium text-slate-700 underline decoration-slate-300 underline-offset-2 hover:decoration-primary"
                            title="Select one or more people"
                        >
                            or pick your favorites
                        </Link>
                    </p>

                    <ConversationStartersList class="mt-5" :starters="starters" @selected="submitQuery($event)" />
                </div>
                <!-- End Search -->

                <div class="mx-auto mt-8 w-full min-w-0 max-w-2xl px-4 sm:px-6 lg:px-8">
                    <TimelineComponent :hideWhenEmpty="true" />
                </div>

                <div class="mt-12"></div>
            </div>

            <footer class="mx-auto mt-auto max-w-4xl px-4 pt-8 text-center sm:px-6 lg:px-8 2xl:pt-0">
                <p class="text-xs text-slate-600">
                    © {{ year }}. A product of
                    <a
                        class="font-semibold text-slate-900 decoration-2 hover:underline dark:text-slate-300"
                        href="https://spartner.software/"
                        target="_blank"
                        title="Spartner"
                    >
                        Spartner</a
                    >.
                    <Link
                        class="text-slate-900 decoration-2 hover:underline dark:text-slate-300"
                        :href="route('clipfinder.privacy')"
                        target="_blank"
                        title="Privacy Policy"
                    >
                        Privacy Policy
                    </Link>
                </p>
                <!--                    <p class="text-xs text-slate-600">-->
                <!--                        <Link-->
                <!--                            class="font-semibold text-slate-900 decoration-2 hover:underline dark:text-slate-300"-->
                <!--                            :href="route('clipfinder.terms')"-->
                <!--                            target="_blank"-->
                <!--                            title="Terms"-->
                <!--                        >-->
                <!--                            Terms-->
                <!--                        </Link>-->
                <!--                    </p>-->
            </footer>
        </div>
        <!-- End Content -->
    </Layout>
</template>

<script setup lang="ts">
import Layout from '@/Layouts/Layout.vue'
import { Link } from '@inertiajs/vue3'
import { inject, ref, computed, type Ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import SearchInput from '@/Components/SearchInput.vue'
import { router } from '@inertiajs/vue3'
import PodcastclipfinderIcon from '@/Icons/PodcastclipfinderIcon.vue'
import GoogleLoginButton from '@/Components/GoogleLoginButton.vue'
import EmailLoginButton from '@/Components/EmailLoginButton.vue'
import ConversationStartersList from '@/Pages/Podcaster/ConversationStartersList.vue'
import { useConversationStore } from '@/Stores/conversation'
import { ScrollState } from '@/Enum/ScrollState'
import type { ConversationStarterResource } from '@/types'
import { defineUserStore, useUserStore } from '@/Stores/user'
import TimelineComponent from '@/Components/TimelineComponent.vue'
import { usePodcasterStore } from '@/Stores/podcaster'

const route = inject('route')
defineProps<{
    podcasts: number
    hours: number
    totalPodcastersCount: number
    starters: ConversationStarterResource[]
}>()

defineUserStore()

const conversationStore = useConversationStore()
const podcasterStore = usePodcasterStore()

const { sendMessage } = conversationStore

const { scrollState } = storeToRefs(conversationStore)

const userStore = useUserStore()
const { user, loggedIn } = storeToRefs(userStore)

const question: Ref<string | null> = ref(null)
const errors = ref<{ string: string[] }[]>([])
const failed = ref<Boolean>(false)

const submit = () => {
    submitQuery(question.value || '')
}

const submitQuery = async (query: string) => {
    try {
        const conversation = await sendMessage(query, true)
        router.visit(route('clipfinder.conversations.show', [conversation]))
    } catch (e: any) {
        failed.value = true
        errors.value = e.errors || []
    }
}

const isWaiting = computed(() => scrollState.value === ScrollState.Requesting)

const year = computed((): number => {
    return new Date().getFullYear()
})

onMounted(() => {
    podcasterStore.fetchAllPodcasters()
})
</script>
<style>
html {
    overflow-y: scroll; /* Always show vertical scrollbar */
}
</style>
